// frontend/packages/business-console/src/pages/Scheduling/WeeklyHoursEditor.jsx
//
// Net-new weekly-hours editor for a single staff member's schedule. There is
// no existing hours UI in the Console to reuse. It edits one CrmSchedule per
// staff (Phase A: one schedule each): 7 day rows, each with zero or more time
// windows via native <input type="time">, plus date-specific override rows via
// <input type="date">. Times are stored as LOCAL wall-clock minutes since
// midnight (0..1440), matching the backend rule model (availability rules keep
// local time + IANA zone, never UTC).
//
// Save builds the flat rules[] the API expects: one weekly rule per (day,
// window) with days:[day], and one override rule per (date, window). It calls
// updateSchedule when a schedule exists, else createSchedule with the staff's
// time zone.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import {
  listSchedules,
  createSchedule,
  updateSchedule,
} from '../../services/schedulingService';
import { Surface, useTokens } from '../../components/primitives';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function serverMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

function minutesToTime(min) {
  const m = Math.max(0, Math.min(1440, Number(min) || 0));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function timeToMinutes(value) {
  if (!value || typeof value !== 'string' || !value.includes(':')) return null;
  const [h, m] = value.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

// Turn the API rules[] into the editor's { weekly, overrides } shape.
function rulesToState(rules = []) {
  const weekly = DAY_LABELS.map((_, day) => ({ day, windows: [] }));
  const overrides = [];
  for (const r of rules) {
    const win = {
      start: minutesToTime(r.startMinute),
      end: minutesToTime(r.endMinute),
    };
    if (r.kind === 'override') {
      overrides.push({ date: r.date || '', ...win });
    } else {
      const days = Array.isArray(r.days) && r.days.length ? r.days : [];
      for (const d of days) {
        if (weekly[d]) weekly[d].windows.push({ ...win });
      }
    }
  }
  return { weekly, overrides };
}

// Flatten the editor state back to the API rules[].
function stateToRules(weekly, overrides) {
  const rules = [];
  for (const row of weekly) {
    for (const w of row.windows) {
      const startMinute = timeToMinutes(w.start);
      const endMinute = timeToMinutes(w.end);
      if (startMinute == null || endMinute == null) continue;
      rules.push({
        kind: 'weekly',
        days: [row.day],
        date: null,
        startMinute,
        endMinute,
      });
    }
  }
  for (const o of overrides) {
    const startMinute = timeToMinutes(o.start);
    const endMinute = timeToMinutes(o.end);
    if (!o.date || startMinute == null || endMinute == null) continue;
    rules.push({
      kind: 'override',
      days: [],
      date: o.date,
      startMinute,
      endMinute,
    });
  }
  return rules;
}

// Reject any window whose end is not after its start before we hit the server.
function hasInvalidWindow(weekly, overrides) {
  const bad = (start, end) => {
    const s = timeToMinutes(start);
    const e = timeToMinutes(end);
    return s == null || e == null || e <= s;
  };
  for (const row of weekly) {
    for (const w of row.windows) if (bad(w.start, w.end)) return true;
  }
  for (const o of overrides) {
    if (o.date && bad(o.start, o.end)) return true;
  }
  return false;
}

export default function WeeklyHoursEditor({ staff }) {
  const t = useTokens();
  const [scheduleId, setScheduleId] = useState(null);
  const [weekly, setWeekly] = useState(() => rulesToState([]).weekly);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);

  const load = useCallback(async () => {
    if (!staff?._id) return;
    setLoading(true);
    setError(null);
    try {
      const schedules = await listSchedules(staff._id);
      const first = schedules[0] || null;
      setScheduleId(first?._id || null);
      const state = rulesToState(first?.rules || []);
      setWeekly(state.weekly);
      setOverrides(state.overrides);
    } catch (err) {
      setError(serverMessage(err, "Couldn't load this staff member's hours."));
    } finally {
      setLoading(false);
    }
  }, [staff?._id]);

  useEffect(() => {
    load();
  }, [load]);

  const addWindow = (dayIdx) =>
    setWeekly((w) =>
      w.map((row) =>
        row.day === dayIdx
          ? {
              ...row,
              windows: [...row.windows, { start: '09:00', end: '17:00' }],
            }
          : row
      )
    );

  const removeWindow = (dayIdx, winIdx) =>
    setWeekly((w) =>
      w.map((row) =>
        row.day === dayIdx
          ? { ...row, windows: row.windows.filter((_, i) => i !== winIdx) }
          : row
      )
    );

  const setWindow = (dayIdx, winIdx, field, value) =>
    setWeekly((w) =>
      w.map((row) =>
        row.day === dayIdx
          ? {
              ...row,
              windows: row.windows.map((win, i) =>
                i === winIdx ? { ...win, [field]: value } : win
              ),
            }
          : row
      )
    );

  const addOverride = () =>
    setOverrides((o) => [...o, { date: '', start: '09:00', end: '17:00' }]);
  const removeOverride = (idx) =>
    setOverrides((o) => o.filter((_, i) => i !== idx));
  const setOverride = (idx, field, value) =>
    setOverrides((o) =>
      o.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );

  const save = async () => {
    if (saving) return;
    if (hasInvalidWindow(weekly, overrides)) {
      setError('Each time window must end after it starts.');
      return;
    }
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    try {
      const rules = stateToRules(weekly, overrides);
      if (scheduleId) {
        await updateSchedule(scheduleId, { rules });
      } else {
        const created = await createSchedule({
          staffId: staff._id,
          timeZone: staff.timeZone,
          name: 'Weekly hours',
          rules,
        });
        if (created?._id) setScheduleId(created._id);
      }
      setSavedMsg('Hours saved.');
    } catch (err) {
      setError(serverMessage(err, "Couldn't save hours."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Surface sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      </Surface>
    );
  }

  return (
    <Surface sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Weekly hours
          </Typography>
          <Typography variant="caption" sx={{ color: t.muted }}>
            {staff.name} ({staff.timeZone})
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={save}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : null}
        >
          Save hours
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {savedMsg ? (
        <Alert
          severity="success"
          sx={{ mb: 1.5 }}
          onClose={() => setSavedMsg(null)}
        >
          {savedMsg}
        </Alert>
      ) : null}

      <Stack spacing={1}>
        {weekly.map((row) => (
          <Box
            key={row.day}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              py: 0.5,
            }}
          >
            <Typography
              sx={{ width: 44, fontWeight: 700, fontSize: 13, pt: 1 }}
            >
              {DAY_LABELS[row.day]}
            </Typography>
            <Box sx={{ flex: 1 }}>
              {row.windows.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', pt: 1 }}
                >
                  Closed
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {row.windows.map((win, i) => (
                    <Box
                      key={i}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <TimeInput
                        value={win.start}
                        onChange={(v) => setWindow(row.day, i, 'start', v)}
                        ariaLabel={`${DAY_LABELS[row.day]} start`}
                      />
                      <Typography sx={{ color: 'text.secondary' }}>
                        to
                      </Typography>
                      <TimeInput
                        value={win.end}
                        onChange={(v) => setWindow(row.day, i, 'end', v)}
                        ariaLabel={`${DAY_LABELS[row.day]} end`}
                      />
                      <Tooltip title="Remove window">
                        <IconButton
                          size="small"
                          onClick={() => removeWindow(row.day, i)}
                          aria-label="Remove window"
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
            <Button
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={() => addWindow(row.day)}
              sx={{ textTransform: 'none', mt: 0.25 }}
            >
              Add
            </Button>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Date overrides
          </Typography>
          <Typography variant="caption" sx={{ color: t.muted }}>
            Different hours on a specific date. Overrides replace the weekly
            hours for that day.
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={addOverride}
          sx={{ textTransform: 'none' }}
        >
          Add override
        </Button>
      </Box>

      {overrides.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No date overrides.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {overrides.map((o, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box
                component="input"
                type="date"
                value={o.date}
                onChange={(e) => setOverride(i, 'date', e.target.value)}
                aria-label="Override date"
                sx={inputSx(t)}
              />
              <TimeInput
                value={o.start}
                onChange={(v) => setOverride(i, 'start', v)}
                ariaLabel="Override start"
              />
              <Typography sx={{ color: 'text.secondary' }}>to</Typography>
              <TimeInput
                value={o.end}
                onChange={(v) => setOverride(i, 'end', v)}
                ariaLabel="Override end"
              />
              <Tooltip title="Remove override">
                <IconButton
                  size="small"
                  onClick={() => removeOverride(i)}
                  aria-label="Remove override"
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}
    </Surface>
  );
}

function inputSx(t) {
  return {
    font: 'inherit',
    fontSize: 14,
    color: t.text,
    bgcolor: t.card,
    border: `1px solid ${t.cardBorder}`,
    borderRadius: '8px',
    px: 1,
    py: '6px',
    colorScheme: 'light dark',
  };
}

// Native time input styled to sit in the token surface. Native keeps the PR
// dependency-free (no MUI X date pickers).
function TimeInput({ value, onChange, ariaLabel }) {
  const t = useTokens();
  return (
    <Box
      component="input"
      type="time"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      sx={inputSx(t)}
    />
  );
}
